import React, { useEffect, useState } from 'react';
import { Shield, Download, BarChart3, Search, FileText } from 'lucide-react';
import { securitfeeAPI, formatCurrency, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { buildCsvRaw, downloadCsv } from '../utils/csvExport';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

const ReportSecurityFee = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [byOperator, setByOperator] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [appliedMonth, setAppliedMonth] = useState(new Date().getMonth() + 1);
  const [appliedYear, setAppliedYear] = useState(currentYear);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  const applyFilters = () => {
    setAppliedMonth(month);
    setAppliedYear(year);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when applied filters change
  }, [appliedMonth, appliedYear, operatorFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { month: appliedMonth, year: appliedYear };

      const [, byOpRes] = await Promise.all([
        securitfeeAPI.getOverview(params),
        securitfeeAPI.getByOperator(params)
      ]);

      const rawByOp = Array.isArray(byOpRes) ? byOpRes : (byOpRes?.data || []);
      const fullList = rawByOp.map((r) => ({
        operator: r.operatorName || r.operator_name || r.operator,
        totalSubscribers: r.totalSubscribers || r.total_subscribers || 0,
        eligible: Number(r.eligibleSubscribers ?? r.eligible ?? r.eligible_subscribers ?? r.eligible_subscriber_count ?? 0) || 0,
        collected: Number(r.collectedAmount ?? r.collected ?? r.collected_amount ?? 0) || 0,
        remaining: Number(r.remainingAmount ?? r.remaining ?? r.remaining_amount ?? 0) || 0
      }));
      setOperatorOptions(fullList.map((r) => r.operator).filter(Boolean));
      const list = operatorFilter === 'all' ? fullList : fullList.filter((x) => String(x.operator) === String(operatorFilter));
      setByOperator(list);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load security fee report');
      setByOperator([]);
    } finally {
      setLoading(false);
    }
  };

  const reportPeriodLabel = () => {
    const m = MONTHS.find((mo) => mo.value === appliedMonth);
    return `${m?.label ?? appliedMonth} ${appliedYear}`;
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const monthName = MONTHS.find((m) => m.value === appliedMonth)?.label || 'Current';
    const filename = `security-fee-report-${monthName.toLowerCase()}-${appliedYear}`;
    const result = await PDFExporter.exportToPDF('report-security-fee', filename, { scale: 1.5 });
    if (!result.success) alert('Failed to export PDF: ' + result.error);
    setIsExporting(false);
  };

  const totals = {
    collected: byOperator.reduce((s, r) => s + (r.collected || 0), 0),
    remaining: byOperator.reduce((s, r) => s + (r.remaining || 0), 0),
    eligible: byOperator.reduce((s, r) => s + (r.eligible || 0), 0)
  };

  const handleExportCSV = () => {
    const periodLabel = reportPeriodLabel();
    const generated = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Report', 'Security Fee Collection Report'],
      ['Period', periodLabel],
      ['Generated', generated],
      [],
      ['Total Collected', 'Remaining', 'Eligible Subscribers'],
      [totals.collected, totals.remaining, totals.eligible],
      [],
      ['Operator', 'Eligible Subscribers', 'Collected', 'Remaining'],
      ...byOperator.map((r) => [r.operator, r.eligible, r.collected, r.remaining])
    ];
    const csv = buildCsvRaw(rows);
    const filename = `security-fee-report-${periodLabel.replace(/\s/g, '-')}`;
    downloadCsv(filename, csv);
  };

  return (
    <div id="report-security-fee" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Official report header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 print:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="h-8 w-8 text-primary-600" />
                Security Fee Collection Report
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">BAS – Billing Accuracy System</p>
              <p className="text-sm text-gray-600 mt-2">
                Report period: <strong>{reportPeriodLabel()}</strong>
                {operatorFilter !== 'all' && <> · Operator: <strong>{operatorFilter}</strong></>}
              </p>
              <p className="text-xs text-gray-500 mt-1">Generated: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportCSV}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <FileText className="h-4 w-4" />
                Export CSV
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <Download className="h-4 w-4" />
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </button>
            </div>
          </div>
        </div>

        {/* Filters: Month, Year, Operator only */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Month</label>
                <select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Operator</label>
                <select
                  value={operatorFilter}
                  onChange={(e) => setOperatorFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Operators</option>
                  {operatorOptions.map((op) => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={applyFilters}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Search className="h-4 w-4" />
              Apply
            </button>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Report period: <strong className="text-gray-700">{reportPeriodLabel()}</strong>
            {operatorFilter !== 'all' && (
              <> · Operator: <strong className="text-gray-700">{operatorFilter}</strong></>
            )}
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-800">
            <span className="text-red-500 text-lg">Failed to load report.</span>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4" />
            <p className="text-gray-600">Loading security fee report...</p>
          </div>
        ) : (
          <>
            {/* Summary cards – same style as ReportSubscribers */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-green-100 rounded-lg">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Collected</p>
                    <p className="text-2xl font-bold text-green-700 tabular-nums">{formatCurrency(totals.collected)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-100 rounded-lg">
                    <Shield className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Remaining</p>
                    <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatCurrency(totals.remaining)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary-100 rounded-lg">
                    <Shield className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Eligible Subscribers</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatNumber(totals.eligible)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Table by operator – same style as ReportSubscribers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">Collection Summary by Operator</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Operator</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Eligible Subscribers</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Collected</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Remaining</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {byOperator.map((row) => (
                      <tr key={row.operator} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-sm font-medium text-gray-900">{row.operator}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(row.eligible)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(row.collected)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(row.remaining)}</td>
                      </tr>
                    ))}
                    {byOperator.length > 0 && (
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-5 py-3 text-sm text-gray-900">Total</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(totals.eligible)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-green-700">{formatCurrency(totals.collected)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatCurrency(totals.remaining)}</td>
                      </tr>
                    )}
                    {byOperator.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-5 py-10 text-center text-sm text-gray-500">
                          No data for the selected month and year.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportSecurityFee;
